/**
 * Sitecore Search analytics helpers (Cloud SDK).
 *
 * Cloud SDK `getWidgetData` does not auto-track events — callers must fire
 * widgetView / widgetItemClick / facet / suggestion / navigation / entityView.
 * Failures are swallowed so tracking never breaks search UI.
 *
 * Events require a `browser_id` from the Cloud SDK cookie (`enableBrowserCookie: true`).
 */

import {
  entityView,
  widgetFacetClick,
  widgetItemClick,
  widgetNavigationClick,
  widgetSuggestionClick,
  widgetView,
  type EntityViewEventParams,
  type FacetFilterEventParams,
  type SearchEventEntity,
  type SearchEventRequest,
  type SuggestionFilterEventParams,
  type WidgetFacetClickEventParams,
  type WidgetItemClickEventParams,
  type WidgetNavigationClickEventParams,
  type WidgetSuggestionClickEventParams,
  type WidgetViewEventParams,
} from '@sitecore-cloudsdk/search/browser';
import { ensureCloudSdkBrowserForSearch } from 'lib/search/ensure-cloud-sdk-browser';

export type SearchTrackEntity = {
  id: string;
  entity: string;
  uri?: string;
  sourceId?: string;
  entityType?: string;
};

export type SearchTrackRequest = {
  keyword?: string;
  numResults?: number;
  totalResults?: number;
  numRequested?: number;
  pageNumber?: number;
  pageSize?: number;
};

function getPathname(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return window.location.pathname || '/';
}

function getLanguage(): string {
  const locale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';
  return locale.split('-')[0]?.toUpperCase() || 'EN';
}

function toSearchEventEntity(entity: SearchTrackEntity): SearchEventEntity {
  return {
    id: entity.id,
    entity: entity.entity,
    ...(entity.uri ? { uri: entity.uri } : {}),
    ...(entity.sourceId ? { sourceId: entity.sourceId } : {}),
    ...(entity.entityType ? { entityType: entity.entityType } : {}),
  };
}

function toSearchEventRequest(request?: SearchTrackRequest): SearchEventRequest {
  return {
    keyword: request?.keyword,
    numResults: request?.numResults,
    totalResults: request?.totalResults,
    numRequested: request?.numRequested,
    pageNumber: request?.pageNumber,
    pageSize: request?.pageSize,
  };
}

function baseEventFields(page?: string) {
  return {
    pathname: getPathname(),
    language: getLanguage(),
    channel: 'WEB',
    ...(page ? { page } : { page: getPathname() }),
  };
}

async function safeTrack(label: string, run: () => Promise<unknown>): Promise<void> {
  try {
    // Ensure events + search packages are initialized and browser_id cookie exists.
    await ensureCloudSdkBrowserForSearch();
    await run();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[Sitecore Search analytics] ${label} failed`, error);
    }
  }
}

/** Maps Sitecore page templates to Search entity names used in CEC. */
export function resolveSearchEntityFromTemplate(
  templateName?: string | null,
  templateId?: string | null
): string | null {
  const name = templateName?.trim();
  const id = templateId?.toLowerCase();

  switch (name) {
    case 'News Article':
    case 'Expert Comment Page':
      return 'newsarticle';
    case 'Expert Profile Page':
      return 'expertprofile';
    case 'Article Detail Page':
    case 'Article Category Page':
      return 'content';
    case 'Case Study Detail':
      return 'newsarticle';
    default:
      break;
  }

  // Client Story template (see pageMetadata)
  if (id === 'f0591175-f932-4c3b-ac2b-da3623ce5cf1') {
    return 'newsarticle';
  }

  return null;
}

export async function trackSearchWidgetView(params: {
  widgetId: string;
  entities: SearchTrackEntity[];
  request?: SearchTrackRequest;
  page?: string;
}): Promise<void> {
  if (!params.widgetId || typeof window === 'undefined') {
    return;
  }

  const payload: WidgetViewEventParams = {
    ...baseEventFields(params.page),
    widgetId: params.widgetId,
    entities: params.entities.map(toSearchEventEntity),
    request: toSearchEventRequest(params.request),
  };

  await safeTrack('widgetView', () => widgetView(payload));
}

export async function trackSearchItemClick(params: {
  widgetId: string;
  entity: SearchTrackEntity;
  itemPosition: number;
  request?: SearchTrackRequest;
  page?: string;
}): Promise<void> {
  if (!params.widgetId || !params.entity?.id || typeof window === 'undefined') {
    return;
  }

  const payload: WidgetItemClickEventParams = {
    ...baseEventFields(params.page),
    widgetId: params.widgetId,
    entity: toSearchEventEntity(params.entity),
    itemPosition: params.itemPosition,
    request: toSearchEventRequest(params.request),
  };

  await safeTrack('widgetItemClick', () => widgetItemClick(payload));
}

export async function trackSearchFacetClick(params: {
  widgetId: string;
  filters: FacetFilterEventParams[];
  request?: SearchTrackRequest;
  page?: string;
}): Promise<void> {
  if (!params.widgetId || !params.filters?.length || typeof window === 'undefined') {
    return;
  }

  const payload: WidgetFacetClickEventParams = {
    ...baseEventFields(params.page),
    widgetId: params.widgetId,
    filters: params.filters,
    request: toSearchEventRequest(params.request),
  };

  await safeTrack('widgetFacetClick', () => widgetFacetClick(payload));
}

export async function trackSearchNavigationClick(params: {
  widgetId: string;
  itemPosition: number;
  page?: string;
}): Promise<void> {
  if (!params.widgetId || typeof window === 'undefined') {
    return;
  }

  const payload: WidgetNavigationClickEventParams = {
    ...baseEventFields(params.page),
    widgetId: params.widgetId,
    itemPosition: params.itemPosition,
  };

  await safeTrack('widgetNavigationClick', () => widgetNavigationClick(payload));
}

export async function trackSearchSuggestionClick(params: {
  widgetId: string;
  filters: SuggestionFilterEventParams[];
  request?: SearchTrackRequest;
  page?: string;
}): Promise<void> {
  if (!params.widgetId || !params.filters?.length || typeof window === 'undefined') {
    return;
  }

  const payload: WidgetSuggestionClickEventParams = {
    ...baseEventFields(params.page),
    widgetId: params.widgetId,
    filters: params.filters,
    request: toSearchEventRequest(params.request),
  };

  await safeTrack('widgetSuggestionClick', () => widgetSuggestionClick(payload));
}

export async function trackSearchEntityPageView(params: {
  entity: SearchTrackEntity;
  page?: string;
}): Promise<void> {
  if (!params.entity?.id || !params.entity?.entity || typeof window === 'undefined') {
    return;
  }

  const payload: EntityViewEventParams = {
    ...baseEventFields(params.page),
    entity: toSearchEventEntity(params.entity),
  };

  await safeTrack('entityView', () => entityView(payload));
}

export function mapResultsToTrackEntities(
  results: Array<{ id?: string; url?: string; source_id?: string } | null | undefined>,
  entity: string
): SearchTrackEntity[] {
  return results
    .filter((item): item is { id: string; url?: string; source_id?: string } => Boolean(item?.id))
    .map((item) => ({
      id: item.id,
      entity,
      ...(item.url ? { uri: item.url } : {}),
      ...(item.source_id ? { sourceId: item.source_id } : {}),
    }));
}
